/**
 * Sell Manager
 * Handles fish selling and currency transactions
 */

import { FishStack, SellTransaction, GameEvent, isBelowQualityThreshold } from '@/types';
import { getFishDefinition, getBuyerDefinition, QualityMultipliers } from '@/data';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';

export class SellManager {
  /**
   * Sell specific fish stacks to a buyer
   */
  sellFish(fishToSell: FishStack[], buyerId: string): SellTransaction {
    const buyer = getBuyerDefinition(buyerId);
    const state = useGameStore.getState();

    let totalValue = 0;
    const soldFish: FishStack[] = [];

    // Calculate total value and remove from inventory
    for (const stack of fishToSell) {
      // Get fish definition
      const definition = getFishDefinition(stack.definitionId);

      // Calculate value
      const baseValue = definition.baseValue;
      const qualityMult = QualityMultipliers[stack.quality];
      const buyerMult = buyer.priceMultiplier;
      const playerMult = 1 + state.playerStats.valueBonus;

      const valuePerFish = baseValue * qualityMult * buyerMult * playerMult;
      const stackValue = valuePerFish * stack.count;

      totalValue += stackValue;

      // Remove from inventory
      const removed = state.removeFishFromInventory(
        stack.definitionId,
        stack.quality,
        stack.count
      );

      if (removed) {
        soldFish.push({ ...stack });
      }
    }

    // Add currency
    state.addCurrency(totalValue);

    // Update stats
    const totalFishCount = fishToSell.reduce((sum, f) => sum + f.count, 0);
    state.incrementStat('totalFishSold', totalFishCount);

    // Create transaction record
    const transaction: SellTransaction = {
      fish: soldFish,
      buyer: buyerId,
      baseValue: totalValue / (buyer.priceMultiplier || 1),
      bonusMultiplier: buyer.priceMultiplier || 1,
      finalValue: totalValue,
      timestamp: Date.now(),
    };

    // Emit individual fish sold events for quest tracking and stat tracking
    for (const stack of soldFish) {
      const definition = getFishDefinition(stack.definitionId);
      const stackBaseValue = definition.baseValue * QualityMultipliers[stack.quality] * stack.count;
      const stackFinalValue = stackBaseValue * (buyer.priceMultiplier || 1) * (1 + state.playerStats.valueBonus);

      eventBus.emit(GameEvent.FISH_SOLD, {
        fish: [stack],
        buyer: buyerId,
        baseValue: stackBaseValue,
        bonusMultiplier: buyer.priceMultiplier || 1,
        finalValue: stackFinalValue,
        timestamp: Date.now(),
      });
    }

    // Save to localStorage after selling fish
    saveManager.save();

    return transaction;
  }

  /**
   * Sell all fish in inventory (excluding protected fish)
   */
  sellAllFish(buyerId: string): SellTransaction {
    const state = useGameStore.getState();
    const inventory = state.playerState.inventory;
    const autoSellSettings = state.playerState.autoSellSettings;
    const protectedFish = autoSellSettings.protectedFish;
    const threshold = autoSellSettings.qualityThreshold;

    // Filter out protected fish and fish at or above quality threshold
    const sellableFish = inventory.stacks.filter((stack) => {
      // Skip explicitly protected fish (per-fish lock takes priority)
      if (protectedFish[stack.definitionId]?.[stack.quality]) return false;

      // Skip fish at or above quality threshold (player wants to keep these)
      if (threshold && !isBelowQualityThreshold(stack.quality, threshold)) return false;

      return true;
    });

    return this.sellFish(sellableFish, buyerId);
  }

  /**
   * Sell all fish of a specific type and quality
   */
  sellByType(
    definitionId: string,
    quality: string,
    buyerId: string
  ): SellTransaction {
    const state = useGameStore.getState();
    const inventory = state.playerState.inventory;

    const stack = inventory.stacks.find(
      (s) => s.definitionId === definitionId && s.quality === quality
    );

    if (!stack) {
      throw new Error('Fish stack not found in inventory');
    }

    return this.sellFish([stack], buyerId);
  }

  /**
   * Calculate sell value without actually selling (preview)
   */
  calculateValue(fishStack: FishStack, buyerId: string): number {
    const buyer = getBuyerDefinition(buyerId);
    const definition = getFishDefinition(fishStack.definitionId);
    const state = useGameStore.getState();

    const baseValue = definition.baseValue;
    const qualityMult = QualityMultipliers[fishStack.quality];
    const buyerMult = buyer.priceMultiplier;
    const playerMult = 1 + state.playerStats.valueBonus;

    return baseValue * qualityMult * buyerMult * playerMult * fishStack.count;
  }

  /**
   * Calculate total inventory value (excluding protected fish)
   */
  calculateInventoryValue(buyerId: string): number {
    const state = useGameStore.getState();
    const inventory = state.playerState.inventory;
    const autoSellSettings = state.playerState.autoSellSettings;
    const protectedFish = autoSellSettings.protectedFish;
    const threshold = autoSellSettings.qualityThreshold;

    // Only count sellable fish (not protected, not at/above threshold)
    return inventory.stacks.reduce((total, stack) => {
      if (protectedFish[stack.definitionId]?.[stack.quality]) return total;
      if (threshold && !isBelowQualityThreshold(stack.quality, threshold)) return total;
      return total + this.calculateValue(stack, buyerId);
    }, 0);
  }
}

// Singleton instance
export const sellManager = new SellManager();
